import { format, isBefore, parseISO, startOfDay } from "date-fns";
import adapterLanguage from "../../adapterLanguageConfigs";
import defaultTo from "lodash/defaultTo";
import type { DateVal, LocaleLanguages } from "../types";
import { yearRegExpression } from "../../shared/constants/pattern.constant";

export const protectDateFormat = (date?: string): string =>
  date ? format(new Date(date), "dd/MM/yyyy") : "-";

export const getLocaleByCode = (code: LocaleLanguages): Locale => {
  const locale = adapterLanguage.find(
    (l: Record<string, unknown>) => l.code === code
  );
  return locale?.lan as Locale;
};

export const formatDate = (
  date: string | null,
  locale: LocaleLanguages,
  dateFormat?: string
): string => {
  const userLocale = getLocaleByCode(locale);
  if (!date) return "-";

  if (yearRegExpression.test(date)) {
    return date;
  }

  return format(parseISO(date), defaultTo(dateFormat, "P"), {
    locale: userLocale,
  });
};

export const isDatePastIncludingToday = (date: Date): boolean => {
  const today = startOfDay(new Date());

  return isBefore(date, today) || date.getTime() === today.getTime();
};

export const isValidDate = (value: string): boolean => {
  const date = new Date(value);
  return !isNaN(date.getTime());
};

export const formatDateAsPerServer = (
  date: DateVal | string
): string | null => {
  if (!date) {
    return null;
  }
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  return format(parsed, "yyyy-MM-dd");
};

export const validateDateRange = (
  selectedDate: DateVal,
  compareDate: DateVal,
  compareType: "future" | "before",
  compareErrorMessage: string,
  futureErrorMessage: string
): string | true => {
  const today = new Date();

  if (selectedDate && new Date(selectedDate) > today) {
    return futureErrorMessage;
  }

  if (selectedDate && compareDate) {
    if (
      compareType === "before" &&
      new Date(selectedDate) < new Date(compareDate)
    ) {
      return compareErrorMessage;
    }
    if (
      compareType === "future" &&
      new Date(selectedDate) > new Date(compareDate)
    ) {
      return compareErrorMessage;
    }
  }

  return true;
};

export const validateDateLessThan1900 = (date: Date): boolean => {
  if (date && date.getFullYear() < 1900) {
    return true;
  }
  return false;
};

export const validateFutureDate = (date: Date): boolean => {
  const today = new Date();
  if (date && date > today) {
    return true;
  }
  return false;
};

export const isValidDateOrNot = (
  date: Date | null | undefined,
  checkDoBValidations = false
): boolean => {
  if (!date) {
    return false;
  }
  const convertedDate = new Date(date);
  if (
    checkDoBValidations &&
    (validateDateLessThan1900(convertedDate) ||
      validateFutureDate(convertedDate))
  ) {
    return false;
  }
  return !isNaN(convertedDate.getTime());
};

export const isExpired = (date: string): boolean => {
  const currentDate = new Date();
  const expiryDate = new Date(date);
  return currentDate > expiryDate;
};

export interface DurationObject {
  days?: number;
  months?: number;
  years?: number;
}

export const isDurationObject = (
  duration: string | DurationObject
): boolean => {
  if (
    typeof duration === "object" &&
    duration !== null &&
    !Array.isArray(duration)
  ) {
    return "days" in duration || "months" in duration || "years" in duration;
  }
  try {
    const parsed = JSON.parse(duration as string);
    return (
      typeof parsed === "object" &&
      parsed !== null &&
      ("days" in parsed || "months" in parsed || "years" in parsed)
    );
  } catch {
    return false;
  }
};

export const formatDuration = (duration: string | DurationObject): string => {
  let durationObj: DurationObject;
  if (typeof duration === "string") {
    try {
      durationObj = JSON.parse(duration);
    } catch {
      return duration;
    }
  } else {
    durationObj = duration;
  }

  const parts: string[] = [];
  if (durationObj.years) {
    parts.push(
      `${durationObj.years} ${durationObj.years === 1 ? "Year" : "Years"}`
    );
  }
  if (durationObj.months) {
    parts.push(
      `${durationObj.months} ${durationObj.months === 1 ? "Month" : "Months"}`
    );
  }
  if (durationObj.days) {
    parts.push(
      `${durationObj.days} ${durationObj.days === 1 ? "Day" : "Days"}`
    );
  }
  if (parts.length > 0) {
    return parts.join(" ");
  }
  return typeof duration === "string" ? duration : "0 Days";
};
